import React from "react";
import ApiKeyContext from "../contexts/ApiKeyContext";
import CatalogContext from "../contexts/CatalogContext";
import Chat from "../models/Chat";

const ChatContext = React.createContext({
	chats: null,
	currentChat: null,
	currentChatMessage: null,
	addNewChatMessage: null,
	filters: { account: true },
});

export const useChatContext = () => {
	const context = React.useContext(ChatContext);
	if (!context) {
		throw new Error("useChatContext must be used within a ChatContextProvider");
	}
	return context;
};

export const ChatContextProvider = ({ children }) => {
	const apiContext = React.useContext(ApiKeyContext);
	const [catalog] = React.useContext(CatalogContext);

	const [filters, setFilters] = React.useState({ account: true });
	const [chatTarget, setChatTarget] = React.useState(null);
	const [lastOptions, setLastOptions] = React.useState(null);

	const [chats, setChats] = React.useState(null);
	const [chatsLoading, setChatsLoading] = React.useState(null);
	const [currentChat, setCurrentChat] = React.useState(null);
	const [messagesRead, setMessagesRead] = React.useState({});

	const [currentChatMessage, setCurrentChatMessage] = React.useState(null);

	const currentChatCacheRef = `${catalog?.account?.code}/chat/current`;
	const storedChatId = localStorage.getItem(currentChatCacheRef);

	currentChat &&
		localStorage.setItem(`chats/${catalog.id}/current`, currentChat.id);

	const getChats = React.useCallback(
		force => {
			const searchOptions = {
				sort: "created_on DESC",
				paths: JSON.stringify(Chat.SEARCH_PATHS),
				where: {},
				filter: []
			};
			if (filters?.account) {
				searchOptions.where = JSON.stringify({"{account}" : catalog?.account?.id });
			}

			let isChanged = lastOptions !== JSON.stringify(searchOptions);
			if (!chatsLoading && (force || isChanged)) {
				setChatsLoading(true);
				Chat.Search(apiContext, searchOptions)
					.then(results => {
						setChats(results);
						setChatsLoading(false);

						setLastOptions(JSON.stringify(searchOptions));

						const cached_id = localStorage.getItem(
							`chats/${catalog.id}/current`
						);
						!currentChat &&
							cached_id &&
							setCurrentChat(results.find((r) => r.id === cached_id));
						setCurrentChatMessage(null);
					})
					.catch(console.error);
			}
		},
		[
			apiContext,
			catalog?.account?.id,
			catalog.id,
			filters?.account,
			lastOptions,
			chatsLoading,
			currentChat,
		]
	);


	const getChat = React.useCallback(() => {
		!currentChat
			&& storedChatId
			&& storedChatId !== "undefined"
			&& +storedChatId !== +currentChat?.id
			&& Chat.Load(apiContext, storedChatId)
				.then(({ data }) => {
					setCurrentChatMessage(null);
					setCurrentChat(data);
				})
				.catch(console.error);

		return true;
	}, [apiContext, currentChat?.id, storedChatId]);

	// COMMENTED OUT to stop from fetch on all page load
	// React.useEffect(() => {
	// 	window.chatTimeout = window.chatTimeout || setTimeout(getChats, 2000);
	// }, [getChats]);
	React.useEffect(() => {
		getChat()
	}, [currentChat, getChat]);

	const setAsCurrentChat = (chat) => {
		chat?.id && localStorage.setItem(currentChatCacheRef, chat.id);

		setCurrentChat(chat);
		return true;
	};

	let isLoadingChat = false;
	const fetchAndSetCurrent = (chatId) => {
		if (!chatId || isLoadingChat) {
			return true;
		}

		localStorage.setItem(currentChatCacheRef, chatId);
		Chat.Load(apiContext, chatId).then((chat) => setCurrentChat(chat.data));
		isLoadingChat = true;

		return true;
	};

	const setCurrentChatCacheRef = (chatId) =>
		localStorage.setItem(currentChatCacheRef, chatId);

	const clearCurrentChatCacheRef = () => {
		localStorage.removeItem(currentChatCacheRef);
		setCurrentChatMessage(null);
		setCurrentChat(null);
		setChats(null);
		getChats();
	};

	const addNewChatMessage = message => {
		// Find current chat, or create if unavailable
		const currentAccountId = catalog.account.id;
		let chat = chats && chats.find(
			needle =>
				needle.account.id === currentAccountId &&
				needle.status &&
				!needle.is_closed &&
				!needle.is_resolved
		);
		setCurrentChatMessage(message);
		setCurrentChat(chat);
	};

	const value = {
		chats,
		currentChat,
		setChats,
		getChats,
		setCurrentChat,
		setAsCurrentChat,
		setCurrentChatCacheRef,
		clearCurrentChatCacheRef,
		filters,
		setFilters,
		chatTarget,
		setChatTarget,
		fetchAndSetCurrent,
		currentChatMessage,
		setCurrentChatMessage,
		addNewChatMessage,
		messagesRead,
		setMessagesRead,
	};

	return (
		<ChatContext.Provider value={value} displayName="Chat Context">
			{children}
		</ChatContext.Provider>
	);
};
